import { useEffect, useRef, useState } from "react";
import { endpoints, API_BASE } from "@/lib/api";
import { adStyles } from "@/lib/adSizes";
import { useAuth } from "@/context/AuthContext";

const abs = (u) => (u && u.startsWith("/api/") ? `${API_BASE}${u}` : u);

// Renders admin-managed ads for a placement + position. Positions let Admin control WHERE on the
// page an ad shows (top / bottom); legacy ads with no position default to "top". Impressions are
// counted once per mount; clicks tracked. Nothing about placement is hard-coded here — it is fully
// driven by what Admin selects in Advertisement Management.
export const AdSlot = ({ placement, position = "top", className = "", max = 2 }) => {
  const [ads, setAds] = useState([]);
  const seen = useRef(new Set());
  const { user, isSubscribed } = useAuth();
  const adFree = isSubscribed && user?.tier === "premium"; // Premium membership = ad-free

  useEffect(() => {
    if (adFree) return undefined;
    let alive = true;
    endpoints.publicAds(placement).then((r) => {
      if (!alive) return;
      const list = (r.data?.ads || [])
        .filter((a) => (a.position || "top") === position)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .slice(0, max);
      setAds(list);
      list.forEach((a) => { if (!seen.current.has(a.id)) { seen.current.add(a.id); endpoints.adImpression(a.id).catch(() => {}); } });
    }).catch(() => {});
    return () => { alive = false; };
  }, [placement, position, max, adFree]);

  if (adFree || !ads.length) return null;

  const onClick = (a) => { endpoints.adClick(a.id).catch(() => {}); if (a.link) window.open(a.link, "_blank", "noopener"); };

  return (
    <div className={`w-full space-y-3 ${className}`} data-testid={`adslot-${placement}-${position}`}>
      {ads.map((a) => {
        const { wrapStyle, mediaStyle } = adStyles(a);
        return (
          <div key={a.id} style={wrapStyle} onClick={() => onClick(a)} data-testid={`ad-${a.id}`}
            className={`relative rounded-2xl overflow-hidden border border-[#273042] bg-[#0A0C10] ${a.link ? "cursor-pointer" : ""} group`}>
            <span className="absolute top-2 left-2 z-10 text-[9px] uppercase tracking-wider bg-black/60 text-slate-300 px-2 py-0.5 rounded">Ad{a.advertiser ? ` · ${a.advertiser}` : ""}</span>
            {a.mediaType === "video"
              ? <video src={abs(a.mediaUrl)} poster={abs(a.poster)} autoPlay muted loop playsInline preload="metadata" style={mediaStyle} className="block w-full" />
              : <img src={abs(a.mediaUrl)} alt={a.name} loading="lazy" style={mediaStyle} className="block w-full" />}
          </div>
        );
      })}
    </div>
  );
};
